import { useCallback, useEffect, useState } from "react";
import { getDocTypes } from "../../api/client";
import { uiCodesForBackendDocTypes } from "../doc-catalog";

export interface UseAllowedDocCodesResult {
  /** Display ``code``s the signed-in user's tier may draft, for
   *  ``DoctypePicker``'s ``allowedDocCodes``. ``null`` only while the first
   *  fetch is in flight — the picker should treat null as "still loading".
   *  Once resolved it is a concrete list (empty on error = fail-closed). */
  allowedDocCodes: string[] | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Resolve which doctypes the doctype picker may show, straight from the
 * backend's tier-filtered ``GET /doc-types``.
 *
 * The server is the sole authority: it returns only the
 * ``doctypes/doc_pricing.json`` doctypes for a ``free_user`` and everything for
 * other tiers, so the add-in adds no allowlist or tier logic of its own — it
 * translates the returned backend codes to the picker's display codes
 * (:func:`uiCodesForBackendDocTypes`) and shows exactly that.
 *
 * Fail-closed: before the first fetch resolves ``allowedDocCodes`` is ``null``
 * (loading — the picker shows nothing draftable yet, never the full catalog),
 * and a failed fetch settles to ``[]`` rather than a baked-in fallback set.
 */
export function useAllowedDocCodes(): UseAllowedDocCodesResult {
  const [allowedDocCodes, setAllowedDocCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const entries = await getDocTypes();
      const codes = uiCodesForBackendDocTypes(entries.map((e) => e.code));
      setAllowedDocCodes(codes);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn("[useAllowedDocCodes] getDocTypes failed:", err);
      setError(message);
      // Fail-closed: an unreadable catalogue narrows the picker to nothing
      // rather than falling open to every doctype.
      setAllowedDocCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { allowedDocCodes, loading, error, refresh };
}
